import { ColoredIcon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import type { ChildrenProps } from "@msuite/ui/types";
import { COLORS } from "@msuite/utils/colors";
import { ICONS } from "@msuite/utils/icons";
import type { Enums } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";

/** Props Interface */
interface TableTypeMenuProps extends ChildrenProps {
	index: number;
	onUpdateIcon?: (icon: Enums<"iconName">) => void;
	onUpdateColor?: (color: Enums<"color">) => void;
	withIconSelect: boolean;
}

export function TableTypeMenu({
	children,
	index,
	onUpdateColor,
	onUpdateIcon,
	withIconSelect,
}: TableTypeMenuProps) {
	/** Context */
	const methods = useFormContext();
	const tableType = useWatch({
		control: methods.control,
		name: `types.${index}`,
	});

	/** Hooks */
	const { t } = useTranslation();

	/** Functions */
	function handleOnChangeColor(color: Enums<"color">) {
		if (onUpdateColor) onUpdateColor(color);
	}

	function handleOnChangeIcon(icon: Enums<"iconName">) {
		if (onUpdateIcon) onUpdateIcon(icon);
	}

	const colorItem = {
		label: t("theme.color"),
		onClick: undefined,
		icon: icon("Palette"),
		cyId: "table-type-icon-color",
		items: [
			...COLORS.map((color) => ({
				label: t(`theme.${color}`),
				icon: () => (
					<ColoredIcon
						iconName={withIconSelect ? tableType.icon : "circle"}
						color={color}
					/>
				),
				onClick: () => handleOnChangeColor(color),
				isActive: tableType.color === color,
				keepOpen: true,
				cyId: "table-type-icon-color-item",
			})),
		],
	};

	/** Render */
	return (
		<Menu
			className="mr-1"
			items={() => [
				withIconSelect
					? {
							label: t("theme.icon"),
							onClick: undefined,
							icon: icon("Atom"),
							items: [
								...ICONS.map((icon) => ({
									label: icon,
									icon: () => (
										<ColoredIcon
											iconName={icon}
											color={tableType.color}
										/>
									),
									onClick: () => handleOnChangeIcon(icon),
									isActive: tableType?.icon === icon,
									keepOpen: true,
								})),
							],
						}
					: null,
				withIconSelect ? colorItem : null,
				...(!withIconSelect ? colorItem.items : []),
			]}
		>
			{children}
		</Menu>
	);
}
